/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/*
 * phoneIndex — the dedup gate that turns a verified phone number into a 1:1
 * mapping with a Firebase uid. Same role for "one person per account" that
 * /usernames plays for "one handle per account", but the rule shape is
 * stricter: single-key reads only, never enumerable (no parent .read).
 *
 * Outcomes after Firebase Phone Auth confirms an OTP:
 *   - free       → no row exists. Claim it atomically with the phoneNumber
 *                  field on the user profile.
 *   - self       → row exists and points to the caller (re-entry on the same
 *                  uid, e.g. user re-verifies a phone they already own).
 *   - collision  → row exists and points to a different uid. Same real person
 *                  signing in via a new provider. Phase 1 blocks the
 *                  duplicate; the seamless linkWithCredential follow-up is
 *                  deferred.
 *
 * Production code path: call claimPhoneIndex AFTER linkWithCredential has
 * attached the phone to the JS-side auth user. Order matters — if we claim
 * the index first and the link then fails, the phoneNumber field and the
 * /phoneIndex row would be stranded against an unlinked auth user.
 */

import {
  get,
  ref,
  runTransaction,
  update,
  type Database,
} from "firebase/database";

export type PhoneIndexClaimResult =
  | { kind: "claimed" }
  | { kind: "self" }
  | { kind: "collision"; existingUid: string };

/**
 * Atomically claim /phoneIndex/{e164} for `uid`, returning what happened.
 *
 * The free-claim decision runs inside a `runTransaction` on the index node so
 * two devices verifying the same number concurrently cannot both read "free"
 * and both write — exactly one transaction commits the claim; the loser sees
 * the now-occupied value and reports `self` or `collision`. The previous
 * get()-then-update() pair had a read-to-write window where both writers
 * passed the "free" check (Build 129 race fix).
 *
 * Orphan recovery (a stale row pointing at a deleted account) needs an async
 * profile read, which a transaction callback cannot perform, so it is handled
 * as a rare pre-transaction branch below. The /phoneIndex security rule is the
 * real gate there: it only permits overwriting a row whose owner has no
 * /users/{uid}/profile node, so a spoofed orphan-overwrite still fails
 * server-side.
 *
 * `e164` must already be in E.164 form. Validate with normalizeToE164
 * (utils/phone.ts) before calling.
 */
export async function claimPhoneIndex(
  database: Database,
  e164: string,
  uid: string,
): Promise<PhoneIndexClaimResult> {
  if (!e164.startsWith("+")) {
    throw new Error(`claimPhoneIndex: expected E.164 (got "${e164}")`);
  }
  if (!uid) {
    throw new Error("claimPhoneIndex: uid is required");
  }

  const indexRef = ref(database, `phoneIndex/${e164}`);

  // Atomically claim the index row. The callback runs against the latest
  // committed value, retried by the SDK on contention, so concurrent claims
  // serialize: only the first writer transitions null → uid.
  let outcome: PhoneIndexClaimResult = { kind: "self" };
  const tx = await runTransaction(indexRef, (current) => {
    if (current === null) {
      outcome = { kind: "claimed" };
      return uid; // claim the free row
    }
    if (current === uid) {
      outcome = { kind: "self" };
      return current; // owner re-verifying — no change
    }
    outcome = { kind: "collision", existingUid: String(current) };
    return current; // someone else owns it — leave untouched
  });

  // Defensive: if the transaction did not commit for a reason other than our
  // explicit no-op returns, fall back to the committed snapshot's value.
  if (!tx.committed && outcome.kind === "claimed") {
    const val = tx.snapshot.val();
    outcome =
      val === uid
        ? { kind: "self" }
        : { kind: "collision", existingUid: String(val) };
  }

  if (outcome.kind === "claimed") {
    // Mirror the claim into the profile field so the phone gate unmounts.
    // Self-write; permitted by the rule.
    await update(ref(database), { [`users/${uid}/phoneNumber`]: e164 });
    return outcome;
  }

  if (outcome.kind === "self") {
    return outcome;
  }

  // collision — the row points at a different uid. Build 117 orphan
  // detection: if that uid's /users/{uid}/profile no longer exists, the row
  // is stale (account deleted before phoneIndex cleanup landed). Overwrite it
  // — the security rule independently verifies the owner is profile-less, so
  // a spoof can't succeed here.
  const existingUid = outcome.existingUid;
  let canonicalExists = true;
  try {
    const profileSnap = await get(
      ref(database, `users/${existingUid}/profile`),
    );
    canonicalExists = profileSnap.exists();
  } catch {
    // Read failure — be conservative, treat as still-claimed so we never
    // silently steal a legit account's phone on a transient read glitch.
    canonicalExists = true;
  }
  if (canonicalExists) {
    return outcome; // genuine live collision
  }

  // Orphan overwrite. Multi-path update so the index row and the profile
  // field land together. The rule enforces uid === auth.uid here too.
  await update(ref(database), {
    [`phoneIndex/${e164}`]: uid,
    [`users/${uid}/phoneNumber`]: e164,
  });
  return { kind: "claimed" };
}
